import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { FaCheck } from 'react-icons/fa6';
import { SubscriptionType } from '../../../types/misc';
import { baseSubscriptionPrices, subscriptionFeatures, useSubscriptionOptions, variableSubscriptionPrices } from '../../../utils/subscriptionUtils';

export type SubscriptionDisplayMode = 'cards' | 'selectable';

export interface SubscriptionSelectorProps {
  selectedSubscription?: SubscriptionType | null;
  onSubscriptionSelect?: (subscriptionType: SubscriptionType) => void;
  mode: SubscriptionDisplayMode;
  showTrialPeriod?: boolean;
  trialDays?: number;
  showActionButtons?: boolean;
  selectedActionText?: string;
  unselectedActionText?: string;
  onActionClick?: (subscriptionType: SubscriptionType) => void;
  className?: string;
}

export function SubscriptionSelector({
  selectedSubscription,
  onSubscriptionSelect,
  mode,
  showTrialPeriod = false,
  trialDays = 30,
  showActionButtons = false,
  selectedActionText,
  unselectedActionText,
  onActionClick,
  className,
}: SubscriptionSelectorProps) {
  const { t } = useTranslation('', { keyPrefix: 'page.onboarding.subscription' });
  const { t: tSettings } = useTranslation('', { keyPrefix: 'page.settings.subscriptions' });
  const subscriptionOptions = useSubscriptionOptions();

  const getCardClassName = (subscriptionType: SubscriptionType) => {
    const baseClasses = 'relative rounded-xl border-2 p-6';
    
    if (mode === 'cards') {
      return classNames(baseClasses, 'flex flex-col h-full', {
        'opacity-50': subscriptionType === SubscriptionType.PRO,
        'border-primary-900 bg-opacity-10 shadow-md': selectedSubscription === subscriptionType,
        'border-secondary-200 bg-secondary-200 bg-opacity-30': selectedSubscription !== subscriptionType && subscriptionType !== SubscriptionType.PRO,
        'border-gray-500 bg-gray-300': subscriptionType === SubscriptionType.PRO,
      });
    }
    
    return classNames(baseClasses, 'group transition-all duration-300', {
      'cursor-pointer hover:shadow-lg': subscriptionType !== SubscriptionType.PRO,
      'cursor-not-allowed opacity-50': subscriptionType === SubscriptionType.PRO,
      'border-primary-900 bg-opacity-10 shadow-md': selectedSubscription === subscriptionType,
      'border-secondary-200 bg-secondary-200 bg-opacity-30 hover:border-primary-900 hover:bg-opacity-40': selectedSubscription !== subscriptionType && subscriptionType !== SubscriptionType.PRO,
      'border-gray-500 bg-gray-300': subscriptionType === SubscriptionType.PRO,
    });
  };

  const renderTopBadge = (subscriptionType: SubscriptionType) => {
    if (mode === 'cards' && selectedSubscription === subscriptionType) {
      return (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="bg-primary-900 text-secondary-200 px-3 py-1 rounded-full text-xs font-bold">
            {tSettings('activeSubscription')}
          </div>
        </div>
      );
    }
    
    if (subscriptionType === SubscriptionType.BASE) {
      return (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="bg-primary-900 text-secondary-200 px-3 py-1 rounded-full text-xs font-bold">
            {t('mostChosen')}
          </div>
        </div>
      );
    }
    
    if (subscriptionType === SubscriptionType.PRO) {
      return (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="bg-gray-400 text-gray-900 px-3 py-1 rounded-full text-xs font-bold">
            {t('comingSoon')}
          </div>
        </div>
      );
    }
    
    return null;
  };

  const renderSelectionIndicator = (subscriptionType: SubscriptionType) => {
    if (mode !== 'selectable') return null;
    
    return (
      <div
        className={classNames(
          'absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full border-2 transition-all duration-200',
          {
            'border-primary-900 bg-primary-900': selectedSubscription === subscriptionType,
            'border-secondary-200 group-hover:border-primary-300': selectedSubscription !== subscriptionType && subscriptionType !== SubscriptionType.PRO,
            'border-gray-500 bg-gray-400': subscriptionType === SubscriptionType.PRO,
          }
        )}>
        {selectedSubscription === subscriptionType && (
          <FaCheck className="h-3 w-3 text-secondary-50" />
        )}
      </div>
    );
  };

  const renderPricing = (subscriptionType: SubscriptionType) => {
    if (showTrialPeriod && subscriptionType !== SubscriptionType.FREE) {
      return (
        <div className="flex items-baseline gap-1">
          <div className="flex flex-col items-start">
            <span className="text-2xl font-bold text-primary-900">
              {t('freePeriod', { days: trialDays.toString() })}
            </span>
            <span className="text-xs opacity-75 text-primary-900">
              {`${t('afterFreePeriod')} €${baseSubscriptionPrices[subscriptionType].toFixed(2)}/${t('perMonth')}`}
            </span>
          </div>
        </div>
      );
    }
    
    return (
      <div className="flex items-baseline gap-1">
        <span className={classNames('text-2xl font-bold', {
          'text-primary-900': subscriptionType !== SubscriptionType.PRO,
          'text-gray-900': subscriptionType === SubscriptionType.PRO,
        })}>
          €{baseSubscriptionPrices[subscriptionType].toFixed(2)}
        </span>
        <span className={classNames('text-sm opacity-75', {
          'text-primary-900': subscriptionType !== SubscriptionType.PRO,
          'text-gray-900': subscriptionType === SubscriptionType.PRO,
        })}>
          /{t('perMonth')}
        </span>
      </div>
    );
  };

  const renderActionButton = (subscriptionType: SubscriptionType) => {
    if (!showActionButtons || subscriptionType === SubscriptionType.PRO) {
      return null;
    }
    
    const isSelected = selectedSubscription === subscriptionType;
    const buttonText = isSelected ? selectedActionText : unselectedActionText;
    
    if (!buttonText || !onActionClick) return null;
    
    return (
      <div className="mt-6">
        <button
          onClick={() => onActionClick(subscriptionType)}
          type="button"
          className="w-full rounded-lg border-2 px-4 py-2 text-center text-sm font-bold transition-all border-primary-900 bg-primary-900 hover:bg-primary-100 text-secondary-200 hover:bg-primary-400">
          {buttonText}
        </button>
      </div>
    );
  };

  const handleCardClick = (subscriptionType: SubscriptionType) => {
    if (mode === 'selectable' && subscriptionType !== SubscriptionType.PRO && onSubscriptionSelect) {
      onSubscriptionSelect(subscriptionType);
    }
  };

  return (
    <div className={classNames('grid grid-cols-1 md:grid-cols-3 gap-6', className)}>
      {subscriptionOptions.map((subscriptionType) => (
        <div
          key={subscriptionType}
          onClick={() => handleCardClick(subscriptionType)}
          className={getCardClassName(subscriptionType)}>
          
          {renderTopBadge(subscriptionType)}
          {renderSelectionIndicator(subscriptionType)}

          <div className={mode === 'cards' ? 'flex-1 flex flex-col' : ''}>
            <div className="mb-4">
              <h3 className={classNames('text-xl font-bold mb-2', {
                'text-primary-900': subscriptionType !== SubscriptionType.PRO,
                'text-gray-900': subscriptionType === SubscriptionType.PRO,
              })}>
                {t(`${subscriptionType.toLowerCase()}.title`)}
              </h3>
              <p className={classNames('text-sm opacity-75 mb-3', {
                'text-primary-900': subscriptionType !== SubscriptionType.PRO,
                'text-gray-900': subscriptionType === SubscriptionType.PRO,
              })}>
                {t(`${subscriptionType.toLowerCase()}.subtitle`)}
              </p>
              
              {renderPricing(subscriptionType)}
              
              {subscriptionType !== SubscriptionType.FREE && (
                <div className="mt-1">
                  <span className={classNames('text-xs opacity-75', {
                    'text-primary-900': subscriptionType !== SubscriptionType.PRO,
                    'text-gray-900': subscriptionType === SubscriptionType.PRO,
                  })}>
                    {tSettings('priceNote', {price: variableSubscriptionPrices[subscriptionType].toFixed(2)})}
                  </span>
                </div>
              )}
            </div>

            <div>
              <h4 className={classNames('font-semibold text-sm', {
                'text-primary-900': subscriptionType !== SubscriptionType.PRO,
                'text-gray-900': subscriptionType === SubscriptionType.PRO,
              })}>
                {t('features')}:
              </h4>
              <div className="space-y-1">
                {subscriptionType === SubscriptionType.BASE && (
                  <div className="pb-2">
                    <span className="text-xs text-primary-900">
                      {t('everythingInFree')}
                    </span>
                  </div>
                )}
                {subscriptionType === SubscriptionType.PRO && (
                  <div className="pb-2">
                    <span className="text-xs text-gray-900">
                      {t('everythingInBase')}
                    </span>
                  </div>
                )}
                {subscriptionFeatures
                  .filter(feature => {
                    if (subscriptionType === SubscriptionType.FREE) return feature[subscriptionType];
                    if (subscriptionType === SubscriptionType.BASE) return feature[subscriptionType] && !feature[SubscriptionType.FREE];
                    if (subscriptionType === SubscriptionType.PRO) return feature[subscriptionType] && !feature[SubscriptionType.BASE];
                    return false;
                  })
                  .map((feature) => (
                    <div key={feature.i18nKey} className="flex items-center gap-2">
                      <div className={classNames('flex h-4 w-4 items-center justify-center rounded-full', {
                        'bg-primary-900': subscriptionType !== SubscriptionType.PRO,
                        'bg-gray-500': subscriptionType === SubscriptionType.PRO,
                      })}>
                        <FaCheck className={classNames('h-2 w-2', {
                          'text-secondary-200': subscriptionType !== SubscriptionType.PRO,
                          'text-gray-900': subscriptionType === SubscriptionType.PRO,
                        })} />
                      </div>
                      <span className={classNames('text-xs', {
                        'text-primary-900': subscriptionType !== SubscriptionType.PRO,
                        'text-gray-900': subscriptionType === SubscriptionType.PRO,
                      })}>
                        {tSettings(feature.i18nKey)}
                        {feature.isComingSoon && (
                          <span className="ml-1 rounded-md bg-primary-900 px-2 py-0.5 text-sm text-secondary-50">
                            {t('comingSoon')}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {renderActionButton(subscriptionType)}
        </div>
      ))}
    </div>
  );
}
